/*
 * MandatoryCourseBlocker — bloqueia o app enquanto o funcionário tiver
 * curso EAD pendente: assinar a lista (OTP) → assistir o vídeo →
 * passar no questionário. Só libera quando tudo estiver "approved".
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import {
  GraduationCap, PenLine, PlayCircle, ListChecks, CheckCircle2, Lock,
} from "lucide-react";
import EmployeeCoursePlayer from "./EmployeeCoursePlayer";
import { AttendanceSignDialog } from "./EmployeeAttendanceConvocations";

const STEP_ORDER = ["needs_signature", "video_pending", "quiz_pending"];

const STEPS = [
  { key: "needs_signature", label: "Assinar a lista de presença", icon: PenLine },
  { key: "video_pending", label: "Assistir ao vídeo do curso", icon: PlayCircle },
  { key: "quiz_pending", label: "Responder o questionário", icon: ListChecks },
];

export default function MandatoryCourseBlocker() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signToken, setSignToken] = useState(null);
  const [playerCourse, setPlayerCourse] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/employee/course-trainings`, {
        params: { token }, timeout: 15000,
      });
      const now = new Date();
      const pend = (r.data?.items || []).filter((it) => {
        if (it.ui_status === "video_pending" || it.ui_status === "quiz_pending")
          return true;
        if (it.ui_status === "needs_signature") {
          if (it.list_status === "closed") return false;
          if (it.expires_at && new Date(it.expires_at) < now) return false;
          return true;
        }
        return false;
      });
      setItems(pend);
    } catch {
      // Fail open: nunca travar o portal por falha de rede.
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading || items.length === 0) return null;

  const current = items[0];
  const stepIdx = STEP_ORDER.indexOf(current.ui_status);

  const handleAction = () => {
    if (current.ui_status === "needs_signature") {
      setSignToken(current.attendance_token);
    } else {
      setPlayerCourse(current.course_key);
    }
  };

  const actionLabel =
    current.ui_status === "needs_signature"
      ? "Assinar lista de presença"
      : current.ui_status === "video_pending"
      ? "Assistir ao vídeo agora"
      : "Responder o questionário";

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="fixed inset-0 z-30 bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="mandatory-course-blocker">
        <motion.div
          initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
              <GraduationCap className="w-6 h-6 text-indigo-700" />
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-slate-900 text-lg">
                Treinamento obrigatório
              </h2>
              <p className="text-xs text-slate-500">
                {items.length > 1
                  ? `Você tem ${items.length} cursos pendentes.`
                  : "Conclua o curso abaixo para liberar o acesso ao painel."}
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
            <div className="font-semibold text-sm text-indigo-900">
              {current.course_title}
            </div>
            {current.training_date && (
              <div className="text-[11px] text-indigo-700 mt-0.5">
                Treinamento: {current.training_date}
                {current.carga_horaria ? ` • ${current.carga_horaria}` : ""}
              </div>
            )}
          </div>

          <div className="space-y-2">
            {STEPS.map((s, i) => {
              const done = i < stepIdx;
              const active = i === stepIdx;
              const Icon = s.icon;
              return (
                <div key={s.key}
                  className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm ${
                    active
                      ? "bg-indigo-600 text-white font-semibold"
                      : done
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-slate-50 text-slate-400"
                  }`}
                  data-testid={`course-blocker-step-${s.key}`}>
                  {done ? (
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                  ) : active ? (
                    <Icon className="w-4 h-4 shrink-0" />
                  ) : (
                    <Lock className="w-4 h-4 shrink-0" />
                  )}
                  <span className="flex-1">{i + 1}. {s.label}</span>
                </div>
              );
            })}
          </div>

          <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900">
            <strong>O acesso ao aplicativo será liberado</strong> assim que
            você concluir todas as etapas (nota mínima {current.pass_threshold || 70}%
            no questionário).
          </div>

          <Button onClick={handleAction}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="course-blocker-action-btn">
            {actionLabel}
          </Button>
        </motion.div>
      </motion.div>

      {signToken && (
        <AttendanceSignDialog
          tokenId={signToken}
          onClose={() => { setSignToken(null); load(); }}
          onSigned={() => { setSignToken(null); load(); }}
        />
      )}

      <EmployeeCoursePlayer
        open={!!playerCourse}
        courseKey={playerCourse}
        onClose={() => { setPlayerCourse(null); load(); }}
      />
    </>
  );
}
